import { useContext } from "react";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { ValueFormatterParams } from "ag-grid-community";
import {
    titleCase,
    onSelectionChangedKeywordRecommendations,
    numericValueFormatter,
} from "@ts/utils";
import { customFilterOptions, mainContext } from "@ts/constants";
import {
    UpdateKeywordManualBid,
} from "@cell-renderers/AllCellRenderers";
import { useSelector } from "react-redux";
import { IMainContext, IRecommendationsData, SubModule, ParentModule } from "@ts/common-types";
import { useGetModulesData } from "@hooks/AllCustomHooks";

const KeywordsFromAuto = () => {
    const { setAutoRecommendationsGridApi, autoRecommendationsLoading } =
        useContext<IMainContext>(mainContext);

    const gridParams = useGetModulesData({
        type: "grid-params",
        activeModuleName: SubModule.KEYWORD_AUTO,
        activeParentModuleName: ParentModule.KEYWORD_RECOMMENDATIONS,
    });

    const keywordsColumnDefs = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "search_term",
                headerName: "Search Term",
                minWidth: 300,
                maxWidth: 450,
                filter: "agTextColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["text"],
                },
                checkboxSelection: true,
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                tooltipField: "custom_search_term",
                valueFormatter: (params: ValueFormatterParams) =>
                    titleCase(params.value || ""),
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                },
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
            },
            {
                field: "bid",
                headerName: "Recommended Bid",
                maxWidth: 200,
                minWidth: 180,
                lockPinned: true,
                cellClass: "lock-pinned",
                cellRenderer: UpdateKeywordManualBid,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["numberWithoutChanges"],
                },
            },
            {
                field: "adgroup_name",
                headerName: "Ad Group",
                filter: "agTextColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "campaign_name",
                headerName: "Campaign",
                filter: "agTextColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                headerName: "Impressions",
                cellStyle: { whiteSpace: "normal" },
                field: "impression",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Clicks",
                cellStyle: { whiteSpace: "normal" },
                field: "click",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 0,
                },
            },
            {
                headerName: "Spends",
                field: "spend",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                sort: "desc",
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Orders",
                field: "orders",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                field: "sale",
                headerName: "Sale",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            // {
            //     headerName: "CTR",
            //     field: "ctr",
            //     filter: "agNumberColumnFilter",
            //     filterParams: {
            //         filterOptions: customFilterOptions["number"],
            //     },
            //     cellRenderer: ChangeInValueCellRenderer,
            //     valueFormatter: numericValueFormatter,
            //     cellRendererParams: {
            //         hasPinnedColumnValue: true,
            //         hasPercent: true,
            //         showChangePercent: true,
            //         decimalPlace: 1,
            //     },
            //     hide: true,
            // },
            // {
            //     headerName: "CR",
            //     cellStyle: { whiteSpace: "normal" },
            //     field: "cr",
            //     filter: "agNumberColumnFilter",
            //     cellRenderer: ChangeInValueCellRenderer,
            //     valueFormatter: numericValueFormatter,
            //     filterParams: {
            //         filterOptions: customFilterOptions["number"],
            //     },
            //     cellRendererParams: {
            //         hasPinnedColumnValue: true,
            //         hasPercent: true,
            //         showChangePercent: true,
            //     },
            //     hide: true,
            // },
            // {
            //     headerName: "CPC",
            //     cellStyle: { whiteSpace: "normal" },
            //     field: "cpc",
            //     filter: "agNumberColumnFilter",
            //     filterParams: {
            //         filterOptions: customFilterOptions["number"],
            //     },
            //     cellRenderer: ChangeInValueCellRenderer,
            //     valueFormatter: numericValueFormatter,
            //     cellRendererParams: {
            //         hasPinnedColumnValue: true,
            //         hasPercent: false,
            //         showChangePercent: true,
            //     },
            //     hide: true,
            // },
            // {
            //     headerName: "CPA",
            //     cellStyle: { whiteSpace: "normal" },
            //     field: "cpa",
            //     filter: "agNumberColumnFilter",
            //     filterParams: {
            //         filterOptions: customFilterOptions["number"],
            //     },
            //     cellRenderer: ChangeInValueCellRenderer,
            //     valueFormatter: numericValueFormatter,
            //     cellRendererParams: {
            //         hasPinnedColumnValue: true,
            //         hasPercent: false,
            //         showChangePercent: true,
            //     },
            //     hide: true,
            // },
            // {
            //     headerName: "ACoS",
            //     field: "acos",
            //     filter: "agNumberColumnFilter",
            //     filterParams: {
            //         filterOptions: customFilterOptions["number"],
            //     },
            //     cellRenderer: ChangeInValueCellRenderer,
            //     valueFormatter: numericValueFormatter,
            //     cellRendererParams: {
            //         hasPinnedColumnValue: true,
            //         hasPercent: true,
            //         showChangePercent: true,
            //     },
            // },
            // {
            //     headerName: "RoAS",
            //     cellStyle: { whiteSpace: "normal" },
            //     field: "roas",
            //     filter: "agNumberColumnFilter",
            //     cellRenderer: ChangeInValueCellRenderer,
            //     filterParams: {
            //         filterOptions: customFilterOptions["number"],
            //     },
            //     valueFormatter: numericValueFormatter,
            //     cellRendererParams: {
            //         hasPinnedColumnValue: true,
            //         hasPercent: false,
            //         showChangePercent: true,
            //     },
            //     hide: true,
            // },
        ],
    });

    const autoRecommendationData = useSelector(
        (state: IRecommendationsData) => state.autoRecommendationData.value
    );

    return (
        <div className="wrapper" style={{ flex: "1 1 0" }}>
            <div
                id="manual-grid"
                className="ag-theme-alpine"
                style={{ height: "98%", width: "100%" }}
            >
                <AgGridLayout
                    rowData={autoRecommendationData}
                    columnDefs={keywordsColumnDefs}
                    setGridApi={setAutoRecommendationsGridApi}
                    onSelectionChanged={
                        onSelectionChangedKeywordRecommendations
                    }
                    loading={autoRecommendationsLoading}
                    isTotalRowsReq={true}
                    rowHeight={gridParams?.rowHeight}
                />
            </div>
        </div>
    );
};

export default KeywordsFromAuto;
